/**
 * Credit mode: Weighted / Actual / Both, the one reading every Vera Sales page
 * applies to lives and groups (docs/design/sales-credit-model.md). Persisted
 * per user in vba_userpreference so it follows them across devices. Default
 * is Actual, so nothing a reader saw yesterday changes until they flip it.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useUserPref } from '@entities/user-preference';
import { CREDIT_MODES, type CreditMode } from '@shared/lib/sales-credit';

interface CreditModeContextValue {
  mode: CreditMode;
  setMode: (mode: CreditMode) => void;
  isLoading: boolean;
}

const CreditModeContext = createContext<CreditModeContextValue | null>(null);

export const CREDIT_MODE_PREF_SCOPE = 'sales.creditMode';
export const CREDIT_MODE_PREF_KEY = 'mode';

export function CreditModeProvider({ children }: { children: ReactNode }) {
  const { value, setValue, isLoading } = useUserPref<CreditMode>(CREDIT_MODE_PREF_SCOPE, CREDIT_MODE_PREF_KEY, 'actual');
  const mode: CreditMode = CREDIT_MODES.includes(value) ? value : 'actual';
  const ctx = useMemo<CreditModeContextValue>(() => ({ mode, setMode: setValue, isLoading }), [mode, setValue, isLoading]);
  return <CreditModeContext.Provider value={ctx}>{children}</CreditModeContext.Provider>;
}

const FALLBACK: CreditModeContextValue = { mode: 'actual', setMode: () => undefined, isLoading: false };

/** The current reading. Outside the provider (tests, previews) it is Actual. */
export function useCreditMode(): CreditModeContextValue {
  return useContext(CreditModeContext) ?? FALLBACK;
}
